import type { DragEvent } from "react";
import { compressImage } from "./compress-image";
import { VIDEO_MAX_BYTES, VIDEO_MAX_SECONDS, VIDEO_MIME_TYPES } from "./media-policy";
import type { MediaPreset } from "./types";

export interface ProcessedAttachment {
	id: string;
	name: string;
	previewUrl: string;
	isVideo: boolean;
	file: File;
	width: number;
	height: number;
}

export interface ProcessAttachmentsResult {
	accepted: ProcessedAttachment[];
	videoTooLarge: number;
	videoTooLong: number;
}

const PROBE_TIMEOUT_MS = 5_000;

// Resolves null when the duration can't be determined (unsupported codec,
// metadata that never loads) — the storage size limit remains the backstop,
// so an unprobeable video must never wedge the composer.
export async function probeVideoDuration(file: File): Promise<number | null> {
	return new Promise((resolve) => {
		const video = document.createElement("video");
		video.preload = "metadata";
		const url = URL.createObjectURL(file);
		let settled = false;
		const settle = (d: number | null) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			URL.revokeObjectURL(url);
			resolve(d);
		};
		const timer = setTimeout(() => settle(null), PROBE_TIMEOUT_MS);
		video.src = url;
		video.muted = true;
		video.onloadedmetadata = () => {
			settle(Number.isFinite(video.duration) ? video.duration : null);
		};
		video.onerror = () => settle(null);
	});
}

export function hasFileTypes(e: DragEvent): boolean {
	const types = e.dataTransfer?.types;
	if (!types) return false;
	for (let i = 0; i < types.length; i++) {
		if (types[i] === "Files") return true;
	}
	return false;
}

function isAcceptedMedia(file: File): boolean {
	if (file.type.startsWith("image/")) return true;
	return (VIDEO_MIME_TYPES as readonly string[]).includes(file.type);
}

export async function processAttachments(files: File[], capacity: number, preset: MediaPreset): Promise<ProcessAttachmentsResult> {
	const filtered = files.filter(isAcceptedMedia);
	const accepted: ProcessedAttachment[] = [];
	let videoTooLarge = 0;
	let videoTooLong = 0;

	for (const file of filtered.slice(0, capacity)) {
		if (file.type.startsWith("video/")) {
			if (file.size > VIDEO_MAX_BYTES) {
				videoTooLarge += 1;
				continue;
			}
			const duration = await probeVideoDuration(file);
			if (duration !== null && duration > VIDEO_MAX_SECONDS) {
				videoTooLong += 1;
				continue;
			}
			accepted.push({
				id: crypto.randomUUID(),
				name: file.name,
				previewUrl: URL.createObjectURL(file),
				isVideo: true,
				file,
				width: 0,
				height: 0,
			});
			continue;
		}

		const { file: processed, width, height } = await compressImage(file, preset);
		accepted.push({
			id: crypto.randomUUID(),
			name: processed.name,
			previewUrl: URL.createObjectURL(processed),
			isVideo: false,
			file: processed,
			width,
			height,
		});
	}

	return { accepted, videoTooLarge, videoTooLong };
}
